import { DefaultBrowserCss, FlexLayout, Typography } from '@eduact/ed-system'
import NumberFormatter from '@Utilities/NumberFormatter'
import stampAutoDir from '@Utilities/StampAutoDir'
import { Icon, Icons } from '@eduact/ed-components'
import Spacer from '@eduact/ed-components/Spacer'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import GapQuestion from '@eduact/ed-components/Test/GapQuestion/GapQuestion'
import { OrderingQuestion } from '@eduact/ed-components/Test/OrderingQuestion'
import EssayQuestion from './EssayQuestion'
import {
	isEssayQuestion,
	isGapQuestion,
	isMcqQuestion,
	isMrqQuestion,
	isOrderingQuestion,
} from '@eduact/ed-components/Test/Question.types'
import McqQuestion from './McqQuestion'
import MrqQuestion from './MrqQuestion'

type Props = {
	question: any
	onChange: (answer: any) => void
	essayQuestionProps?: any
	withNavigation?: boolean
	onNext?: (order: number, index: number) => void
	onPrev?: (order: number, index: number) => void
	index: number
	showNext?: boolean
	showPrev?: boolean
	nextLabel?: string
	prevLabel?: string
	onBlur?: any
	onFocus?: any
	questionLabel?: string
	scoreLabel?: string
}

/**
 * Vendored from ed-components 0.4.10 Test/TestQuestion, unchanged except that
 * the mcq/mrq branches dispatch to the id-keyed local components. Vendoring
 * the wrapper is forced: the package hard-requires its own leaves and exposes
 * no injection seam. Gap/Ordering/Essay stay deep imports from the package.
 */
const TestQuestion: React.FC<Props> = ({
	question,
	essayQuestionProps,
	withNavigation = false,
	onNext,
	onPrev,
	index,
	onChange,
	showNext = true,
	showPrev = true,
	nextLabel = 'Next',
	prevLabel = 'Previous',
	onBlur,
	onFocus,
	questionLabel,
	scoreLabel,
}) => {
	// Gap/Ordering/Essay stay package components that inject question HTML
	// themselves, so the direction stamping happens on the data handed to them
	// (gap content is $$N-split into unbalanced fragments — those get a
	// dir="auto" wrapper instead of per-block stamps).
	const stampedQuestion = useMemo(
		() => (typeof question.content === 'string' ? { ...question, content: stampAutoDir(question.content) } : question),
		[question]
	)
	return (
		<QuestionContainer tabIndex={question.id} onFocus={onFocus} onBlur={onBlur}>
			<QuestionWrapper>
				<QuestionHeader>
					<QuestionOrder dir="auto">{questionLabel ?? `Q${index + 1}.`} </QuestionOrder>
					<QuestionPoints>
						<Points>{scoreLabel ?? 'Points'} </Points>
						<Spacer mx={'0.5rem'} />
						<span>{NumberFormatter(question.weight)}</span>
					</QuestionPoints>
				</QuestionHeader>
				<Spacer mb={{ sm: '1rem', lg: '2rem' }} />
				<div>
					{isGapQuestion(question) && (
						<div dir="auto">
							<GapQuestion label={`Q${question.order}.`} onChange={onChange} question={question} />
						</div>
					)}
					{isOrderingQuestion(question) && (
						<OrderingQuestion onFocus={onFocus} onBlur={onBlur} question={stampedQuestion} onChange={onChange} />
					)}
					{isMcqQuestion(question) && <McqQuestion question={question} onChange={onChange} />}
					{isMrqQuestion(question) && <MrqQuestion question={question} onChange={onChange} />}
					{isEssayQuestion(question) && (
						<EssayQuestion question={stampedQuestion} onChange={onChange} {...essayQuestionProps} />
					)}
				</div>
			</QuestionWrapper>
			{withNavigation && (
				<FlexLayout alignItems={'center'} justifyContent={'space-between'}>
					{showPrev ? (
						<NavigationButton onClick={() => onPrev?.(question.order, index)} alignItems={'center'}>
							<Icon color="primary">
								<Icons.ChevronLeft />
							</Icon>
							<span>{prevLabel}</span>
						</NavigationButton>
					) : (
						<span />
					)}
					{showNext ? (
						<NavigationButton onClick={() => onNext?.(question.order, index)} alignItems={'center'}>
							<span>{nextLabel}</span>
							<Icon color="primary">
								<Icons.ChevronRight />
							</Icon>
						</NavigationButton>
					) : (
						<span />
					)}
				</FlexLayout>
			)}
		</QuestionContainer>
	)
}

export default TestQuestion

const QuestionContainer = styled.div`
	${DefaultBrowserCss};
	img {
		display: block;
		height: auto;
	}
	box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
	background: #fff;
	padding: 1rem;
	border-radius: 8px;
`
const QuestionWrapper = styled.div`
	${({ theme }) => `${theme.mediaQueries.large}{
	padding : 0 8rem;
}`}
`
const QuestionHeader = styled.div`
	display: flex;
	justify-content: space-between;
	border-bottom: 2px solid #d3d3d3;
	padding-bottom: 8px;
	align-items: center;
	${({ theme }) => `${theme.mediaQueries.large}{
		padding-bottom : 2rem;
	}`}
`
const QuestionOrder = styled.span`
	font-size: 10px;
	font-weight: bold;
	${({ theme }) => `${theme.mediaQueries.large}{
		font-size : 20px;
	}`}
`
const QuestionPoints = styled.span`
	font-size: 8px;
	font-weight: bold;
	display: flex;
	align-items: center;
	${({ theme }) => `${theme.mediaQueries.large}{
		font-size : 16px;
	}`}
`
const NavigationButton = styled(FlexLayout).attrs(() => ({
	dir: document.documentElement.dir,
}))`
	color: ${props => props.theme.colors.primary};
	cursor: pointer;
	transition: opacity ease-in-out 300ms;
	&[dir='rtl'] {
		svg {
			transform: rotate(180deg);
		}
	}
	&:hover {
		opacity: 0.8;
	}
`
const Points = styled(Typography)`
	color: ${props => props.theme.colors.silver};
`
